import { useTranslator } from '@Localization';
import { Checkbox, IconButton, Option, Select, SelectOption, SelectOwnProps, SelectStaticProps } from '@mui/joy';
import ArrowDropDown from '@mui/joy/internal/svg-icons/ArrowDropDown';
import CloseIcon from '@mui/joy/internal/svg-icons/Close';
import { FocusEvent, KeyboardEvent, MouseEvent, ReactElement, ReactNode, useRef } from 'react';
interface S_PickySelectProps<T extends object | string | number> extends Omit<SelectOwnProps<T, true>, 'children'> {
  options: { value: T; label: ReactNode }[];
}

export const S_PickySelect = <T extends object | string | number>({ value = [], options, onChange, ...rest }: S_PickySelectProps<T>): ReactElement => {
  const { t } = useTranslator();
  const allValue = '*' as unknown as T;
  const allValueClassName = 'all-option';
  const selectedCount = value.length;
  const optionsCount = options.length;
  const action: SelectStaticProps['action'] = useRef(null);

  const handleChange = (e: MouseEvent | KeyboardEvent | FocusEvent | null, selected: T[]): void => {
    if (e?.target) {
      const element = e.target as Element;
      const parent = element.parentElement?.parentElement?.parentElement;
      if (element.className.includes(allValueClassName) || parent?.className.includes(allValueClassName)) {
        if (selectedCount < optionsCount) {
          onChange?.(
            e,
            options.map((x) => x.value)
          );
        } else {
          onChange?.(e, []);
        }
      } else {
        onChange?.(
          e,
          selected.filter((x) => x !== allValue)
        );
      }
    }
  };

  const selectedValues = optionsCount === selectedCount ? [allValue, ...value] : [...value];

  return (
    <Select
      {...rest}
      slotProps={{
        indicator: {
          component: IconButton,
          size: 'sm',
        },
      }}
      action={action}
      value={selectedValues}
      onChange={handleChange}
      multiple
      renderValue={(selected: SelectOption<T>[]): ReactNode => {
        if (selected.some((x) => x.value === allValue)) {
          return 'All';
        }

        if (selected.length < 4) {
          // eslint-disable-next-line @typescript-eslint/promise-function-async
          return selected.map((x) => x.label).join(', ');
        }
        return <div>{t('x_selected', { count: selected.length })}</div>;
      }}
      indicator={<ArrowDropDown />}
      {...(selectedValues.length && {
        // display the button and remove select indicator
        // when user has selected a value
        endDecorator: (
          <IconButton
            size='sm'
            variant='plain'
            color='neutral'
            className='clear-indicator'
            onMouseDown={(event) => {
              // don't open the popup when clicking on this button
              event.stopPropagation();
            }}
            onClick={(e) => {
              onChange?.(e, []);
              action.current?.focusVisible();
            }}
          >
            <CloseIcon />
          </IconButton>
        ),
      })}
    >
      <Option
        value={allValue}
        className={allValueClassName}
      >
        <Checkbox
          className={allValueClassName}
          checked={selectedValues.some((s) => s === allValue)}
        />
        {t('select_all')}
      </Option>
      {options.map((x) => (
        <Option
          value={x.value}
          key={x.value.toString()}
        >
          <Checkbox checked={selectedValues.some((s) => s === x.value)} />
          {x.label}
        </Option>
      ))}
    </Select>
  );
};
